import React, { useState, useEffect } from 'react';
import './App.css';
import { AppLayout, SideNavigation, TopNavigation } from '@awsui/components-react';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
import { ContentGeneratorComponent } from './components/Content/ContentGeneratorComponent';
import { MyResearchComponent } from './components/Content/MyResearchComponent';
import { LLMPlaygroundComponent } from './components/CI Playground/LLMPlaygroundComponent';
import { AnnouncementsPanel } from './components/Compete Trends/AnnouncementsPanel';
import { ObservationsPanel } from './components/Compete Trends/ObservationsPanel';
import { TrendsPanel } from './components/Compete Trends/TrendsPanel';
import ChatInterface from './components/Chat AI/ChatInterface';
import PromptLibrary from './components/Prompt Library/PromptLibrary';
import SkimUploadPage from './skim/SkimUploadPage';
import SkimHistoryPage from './skim/SkimHistoryPage';
import { getCookie, parseJwt } from './utils/auth';
import config, { COGNITO_GROUPS } from './utils/config';

// Component access scopes — which Cognito groups can see each component.
const COMPONENT_ACCESS = {
  chat: [COGNITO_GROUPS.NON_GCR],
  'prompt-library': [COGNITO_GROUPS.NON_GCR],
  announcements: [COGNITO_GROUPS.NON_GCR],
  observations: [COGNITO_GROUPS.NON_GCR],
  trends: [COGNITO_GROUPS.NON_GCR],
  content: [COGNITO_GROUPS.NON_GCR, COGNITO_GROUPS.GCR],
  'my-research': [COGNITO_GROUPS.NON_GCR, COGNITO_GROUPS.GCR],
  llm: [COGNITO_GROUPS.NON_GCR],
  'skim-upload': [COGNITO_GROUPS.NON_GCR, COGNITO_GROUPS.GCR],
  'skim-history': [COGNITO_GROUPS.NON_GCR, COGNITO_GROUPS.GCR],
};

const hasAccess = (componentId, userGroups) => {
  const allowed = COMPONENT_ACCESS[componentId];
  if (!allowed) return false;
  return allowed.some(group => userGroups.includes(group));
};


const HomePage = () => (
  <div>
    <h1>Welcome to Cloud Intelligence Apps</h1>
    <p>Select an application from the navigation menu to get started.</p>
  </div>
);

// Helper function to set cookies with expiration
const setCookie = (name, value, expiryHours = 1) => {
  const date = new Date();
  date.setTime(date.getTime() + (expiryHours * 60 * 60 * 1000));
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + value + ";" + expires + ";path=/;secure;samesite=strict";
};



function App() {
  const [activeComponent, setActiveComponent] = useState('');
  const [skimBatchId, setSkimBatchId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userGroups, setUserGroups] = useState([]);
  const [userName, setUserName] = useState('');

 // Apply theme based on system preference
  useEffect(() => {
    const applyTheme = (isDark) => {
      applyMode(isDark ? Mode.Dark : Mode.Light);
    };

    // Check initial preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    applyTheme(mediaQuery.matches);

    // Listen for changes
    const handleChange = (e) => applyTheme(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have a code in the URL (from Midway redirect)
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

        if (code) {
          // Exchange the code for tokens with Cognito
          const requestBody = {
            code: code,
            redirect_uri: window.location.origin
          };

          const tokenResponse = await fetch(`${config.cognitoApiUrl()}/GetCognitoClientSecret`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          });

          if (tokenResponse.ok) {
            const responseData = await tokenResponse.json();

            // Parse the inner JSON body if it's a string
            let tokens;
            if (typeof responseData === 'object' && responseData.body) {
              if (typeof responseData.body === 'string') {
                tokens = JSON.parse(responseData.body);
              } else {
                tokens = responseData.body;
              }
            } else {
              tokens = responseData;
            }

            // Store tokens in cookies (expires in 1 hour)
            setCookie('accessToken', tokens.access_token);
            setCookie('idToken', tokens.id_token);
            setCookie('refreshToken', tokens.refresh_token);

            // Extract user groups and other info from the ID token
            const decodedToken = parseJwt(tokens.id_token);
            if (decodedToken) {
              // Set user groups from token
              if (decodedToken['cognito:groups']) {
                console.log('User groups:', decodedToken['cognito:groups']);
                setUserGroups(decodedToken['cognito:groups']);
              }

              // Set user name for display purposes
              if (decodedToken.name) {
                setUserName(decodedToken.name);
              } else if (decodedToken.email) {
                setUserName(decodedToken.email);
              }
            }

            // Remove code from URL after successful token exchange
            window.history.replaceState({}, document.title, window.location.pathname);

            setIsAuthenticated(true);
          } else {
            const errorText = await tokenResponse.text();
            console.error('Token exchange failed:', tokenResponse.status, errorText);
            throw new Error(`Failed to exchange code for tokens: ${tokenResponse.status}`);
          }
        } else {
          // Check if we have valid tokens in cookies
          const accessToken = getCookie('accessToken');
          const idToken = getCookie('idToken');

          if (accessToken && idToken) {
            // Token exists, consider user authenticated
            // Also extract user groups from the ID token
            const decodedToken = parseJwt(idToken);
            if (decodedToken) {
              if (decodedToken['cognito:groups']) {
                setUserGroups(decodedToken['cognito:groups']);
              }

              if (decodedToken.name) {
                setUserName(decodedToken.name);
              } else if (decodedToken.email) {
                setUserName(decodedToken.email);
              }
            }

            setIsAuthenticated(true);
          } else {
            // No token, redirect to Midway
            redirectToMidway();
          }
        }
      } catch (error) {
        console.error('Authentication error:', error);
        redirectToMidway();
      } finally {
        setIsLoading(false);
        // Clear the redirecting flag when we're done
        if (getCookie('accessToken')) {
          sessionStorage.removeItem('redirecting');
        }
      }
    };

    const redirectToMidway = () => {
      // Prevent redirect loops
      if (sessionStorage.getItem('redirecting')) {
        console.log('Already redirecting, preventing loop');
        return;
      }

      sessionStorage.setItem('redirecting', 'true');
      const redirectUri = encodeURIComponent(config.redirectUri());
      const clientId = config.cognitoClientId();
      const cognitoDomain = config.cognitoDomain();
      const midwayUrl = `https://${cognitoDomain}/login?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
      console.log('Redirecting to:', midwayUrl);

      // Open in current window
      window.location.href = midwayUrl;
    };

    checkAuth();
  }, []);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Only render the app if authenticated
  if (!isAuthenticated) {
    return null; // This will never actually render because we redirect in redirectToMidway
  }

  // Base navigation items structure. Sections and links are filtered below
  // based on the user's Cognito groups; sections with no visible items are
  // omitted entirely.
  const allSections = [
    {
      type: 'section',
      text: 'Chat AI',
      items: [
        {
          type: 'link',
          text: 'Chat Interface',
          href: '#chat',
          active: activeComponent === 'chat',
          onFollow: () => setActiveComponent('chat'),
          componentId: 'chat'
        },
        {
          type: 'link',
          text: 'Prompt Library',
          href: '#prompt-library',
          active: activeComponent === 'prompt-library',
          onFollow: () => setActiveComponent('prompt-library'),
          componentId: 'prompt-library'
        }
      ]
    },
    // TODO: Re-enable Compete Trends when ready
    // {
    //   type: 'section',
    //   text: 'Compete Trends',
    //   items: [
    //     { type: 'link', text: 'Announcements', href: '#announcements', active: activeComponent === 'announcements', onFollow: () => setActiveComponent('announcements'), componentId: 'announcements' },
    //     { type: 'link', text: 'Observations', href: '#observations', active: activeComponent === 'observations', onFollow: () => setActiveComponent('observations'), componentId: 'observations' },
    //     { type: 'link', text: 'Trends', href: '#trends', active: activeComponent === 'trends', onFollow: () => setActiveComponent('trends'), componentId: 'trends' }
    //   ]
    // },
    {
      type: 'section',
      text: 'Content',
      items: [
        {
          type: 'link',
          text: 'Battlecard Generator',
          href: '#content',
          active: activeComponent === 'content',
          onFollow: () => setActiveComponent('content'),
          componentId: 'content'
        },
        {
          type: 'link',
          text: 'My Research',
          href: '#my-research',
          active: activeComponent === 'my-research',
          onFollow: () => setActiveComponent('my-research'),
          componentId: 'my-research'
        }
      ]
    },
    {
      type: 'section',
      text: 'SKIM',
      items: [
        {
          type: 'link',
          text: 'Upload Research',
          href: '#skim-upload',
          active: activeComponent === 'skim-upload',
          onFollow: () => setActiveComponent('skim-upload'),
          componentId: 'skim-upload'
        },
        {
          type: 'link',
          text: 'My Uploads',
          href: '#skim-history',
          active: activeComponent === 'skim-history',
          onFollow: () => setActiveComponent('skim-history'),
          componentId: 'skim-history'
        }
      ]
    },
    // TODO: Re-enable CI Playground when ready
    // {
    //   type: 'section',
    //   text: 'CI Playground',
    //   items: [
    //     { type: 'link', text: 'CI Playground', href: '#llm', active: activeComponent === 'llm', onFollow: () => setActiveComponent('llm'), componentId: 'llm' }
    //   ]
    // }
    // TODO: Add SKIM section here when component is ready. Use access scope
    // [COGNITO_GROUPS.NON_GCR, COGNITO_GROUPS.GCR] in COMPONENT_ACCESS above.
  ];

  const baseNavItems = allSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => hasAccess(item.componentId, userGroups)),
    }))
    .filter(section => section.items.length > 0);

  const navigationContent = (
    <SideNavigation
      header={{ text: 'CI Apps', href: '#home' }}
      items={baseNavItems}
      onFollow={event => {
        event.preventDefault();
        const href = event.detail.href;
        if (href === '#chat') {
          setActiveComponent('chat');
        } else if (href === '#prompt-library') {
          setActiveComponent('prompt-library');
        } else if (href === '#announcements') {
          setActiveComponent('announcements');
        } else if (href === '#observations') {
          setActiveComponent('observations');
        } else if (href === '#trends') {
          setActiveComponent('trends');
        } else if (href === '#content') {
          setActiveComponent('content');
        } else if (href === '#my-research') {
          setActiveComponent('my-research');
        } else if (href === '#llm') {
          setActiveComponent('llm');
        } else if (href === '#skim-upload') {
          setActiveComponent('skim-upload');
        } else if (href === '#skim-history') {
          setActiveComponent('skim-history');
        } else if (href === '#home') {
          setActiveComponent('');
        }
      }}
    />
  );

  const renderActiveComponent = () => {
    if (activeComponent && !hasAccess(activeComponent, userGroups)) {
      return <HomePage />;
    }
    switch (activeComponent) {
      case 'chat':
        return <ChatInterface />;
      case 'prompt-library':
        return <PromptLibrary />;
      case 'announcements':
        return <AnnouncementsPanel />;
      case 'observations':
        return <ObservationsPanel />;
      case 'trends':
        return <TrendsPanel />;
      case 'content':
        return <ContentGeneratorComponent />;
      case 'my-research':
        return <MyResearchComponent />;
      case 'llm':
        return <LLMPlaygroundComponent />;
      case 'skim-upload':
        return <SkimUploadPage batchId={skimBatchId} onNavigate={(route, batchId) => {
          setActiveComponent(route);
          if (batchId) setSkimBatchId(batchId);
        }} />;
      case 'skim-history':
        return <SkimHistoryPage onNavigate={(route, batchId) => {
          setActiveComponent(route);
          if (batchId) setSkimBatchId(batchId);
        }} />;
      default:
        return <HomePage />;
    }
  };

  return (
    <div className="App">
      <div id="header" style={{ position: 'sticky', top: 0, zIndex: 1002 }}>
        <TopNavigation
          identity={{
            href: "#",
            title: "Cloud Intelligence Apps",
            onFollow: (e) => {
              e.preventDefault();
              setActiveComponent('home');
            }
          }}
          utilities={[
            {
              type: "button",
              text: userName || "User",
              iconName: "user-profile",
              variant: "link",
              disableUtilityCollapse: true
            }
          ]}
        />
      </div>
      <AppLayout
        navigation={navigationContent}
        content={renderActiveComponent()}
        navigationWidth={250}
        toolsHide={true}
        contentType="default"
        headerSelector="#header"
      />
    </div>
  );
}


export default App;